import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  TextField,
  Divider,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { CloudUpload, CheckCircle, Error, Delete as DeleteIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { resourceService } from '../services/resourceService';
import { resourceTopicService } from '../services/resourceTopicService';
import type { Category } from '../types';
import { validateFile, getDropzoneAcceptConfig, MAX_FILE_SIZE, FILE_VALIDATION_ERRORS } from '../utils/fileValidation';

interface ResourceUploadProps {
  onUploadSuccess?: (resource: any) => void;
  onUploadError?: (error: string) => void;
}

export const ResourceUpload: React.FC<ResourceUploadProps> = ({ onUploadSuccess, onUploadError }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [titles, setTitles] = useState<{ [key: string]: string }>({});
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await resourceTopicService.getTopics({ page: 1, pageSize: 1000 });
      setCategories(response.categories || []);
    } catch (err) {
      console.error('Error loading resource topics:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Auto-generate tags from first file title when files change (only if tags are empty)
  useEffect(() => {
    if (files.length > 0 && tags.length === 0) {
      const firstFile = files[0];
      const firstTitle = titles[firstFile.name] || firstFile.name.substring(0, firstFile.name.lastIndexOf('.'));
      if (firstTitle && firstTitle.trim()) {
        // Split title by common delimiters and create tags
        const words = firstTitle
          .split(/[\s\-_,.]+/)
          .filter(word => word.length > 2) // Only words with more than 2 characters
          .map(word => word.trim())
          .filter(word => word.length > 0);

        // Set tags from title words
        if (words.length > 0) {
          setTags(words);
        }
      }
    }
  }, [files, titles]); // Depend on files and titles

  const handleFilesSelect = (selectedFiles: File[]) => {
    // Validate all files
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    for (const file of selectedFiles) {
      const validation = validateFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    }

    if (invalidFiles.length > 0) {
      setError(`Một số file không hợp lệ: ${invalidFiles.join(', ')}. ${FILE_VALIDATION_ERRORS.INVALID_TYPE}`);
    }

    if (validFiles.length > 0) {
      // Limit to 10 files
      const filesToAdd = validFiles.slice(0, 10);
      setFiles(prev => {
        const combined = [...prev, ...filesToAdd];
        return combined.slice(0, 10); // Max 10 files total
      });
      setError(null);
      setSuccess(null);

      // Auto-fill title from filename for each file
      const newTitles = { ...titles };
      filesToAdd.forEach(file => {
        const fileName = file.name;
        const nameWithoutExtension = fileName.substring(0, fileName.lastIndexOf('.'));
        newTitles[file.name] = nameWithoutExtension;
      });
      setTitles(newTitles);
    }
  };

  const handleFileRemove = (fileName: string) => {
    setFiles(prev => prev.filter(f => f.name !== fileName));
    setTitles(prev => {
      const newTitles = { ...prev };
      delete newTitles[fileName];
      return newTitles;
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0) {
      handleFilesSelect(selectedFiles);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop: (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        setError(`Một số file không hợp lệ. ${FILE_VALIDATION_ERRORS.INVALID_TYPE}`);
      }
      if (acceptedFiles.length > 0) {
        handleFilesSelect(acceptedFiles);
      }
    },
    accept: getDropzoneAcceptConfig(),
    maxFiles: 10,
    maxSize: MAX_FILE_SIZE,
    multiple: true
  });

  const handleTagAdd = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags(prev => [...prev, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleTagAdd();
    }
  };

  const validateForm = (): string | null => {
    if (files.length === 0) return 'Vui lòng chọn ít nhất một file';
    if (files.length > 10) return 'Chỉ được tải lên tối đa 10 file cùng lúc';

    // Validate all files have titles
    const missingTitles = files.filter(f => !titles[f.name]?.trim());
    if (missingTitles.length > 0) {
      return `Vui lòng nhập tiêu đề cho ${missingTitles.length} tài nguyên`;
    }

    // Validate title lengths
    const invalidTitles = files.filter(f => titles[f.name] && titles[f.name].length > 500);
    if (invalidTitles.length > 0) {
      return `Tiêu đề không được vượt quá 500 ký tự cho ${invalidTitles.length} tài nguyên`;
    }

    if (description && description.length > 2000) return 'Mô tả không được vượt quá 2000 ký tự';
    return null;
  };

  const handleUpload = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare form data with categories
      const formData = new FormData();

      // Append all files with individual titles
      files.forEach((file, index) => {
        formData.append('files', file);
        // Send file-specific title (backend supports title_${i})
        const fileTitle = titles[file.name] || file.name.substring(0, file.name.lastIndexOf('.'));
        formData.append(`title_${index}`, fileTitle.trim());
      });

      // Common description (applied to all files)
      if (description) {
        formData.append('description', description);
      }
      if (tags.length > 0) {
        formData.append('tags', tags.join(','));
      }
      if (selectedCategories.length > 0) {
        // Send as comma-separated string instead of JSON to avoid parsing issues
        const categoryIds = selectedCategories.map(c => c.id).join(',');
        formData.append('categoryIds', categoryIds);
      }

      // Get authentication headers
      const headers: HeadersInit = {};
      try {
        const userStr = sessionStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user && user.id) {
            headers['x-user-id'] = user.id;
          }
        }
      } catch (_) {
        // ignore
      }

      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      // Add schoolCode header for tenant context
      try {
        const schoolCode = localStorage.getItem('schoolCode');
        if (schoolCode && String(schoolCode).trim()) {
          headers['x-school-code'] = String(schoolCode).trim();
        }
      } catch (_) {
        // ignore
      }

      // Upload with FormData (use multi-upload endpoint)
      const response = await fetch('/api/resources/upload-multiple', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        let errorMessage = 'Có lỗi xảy ra khi tải lên tài nguyên';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // Use default error message
        }
        throw { message: errorMessage };
      }

      const result = await response.json();
      const uploadedCount = result.successCount || result.resources?.length || 0;
      setSuccess(`Đã tải lên thành công ${uploadedCount} tài nguyên!`);
      setFiles([]);
      setTitles({});
      setDescription('');
      setTags([]);
      setTagInput('');
      setSelectedCategories([]);

      // Reset file input
      const fileInput = document.getElementById('resource-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';

      if (onUploadSuccess) {
        // Call callback with first resource or array of resources
        if (result.resources && result.resources.length > 0) {
          onUploadSuccess(result.resources.length === 1 ? result.resources[0] : result.resources);
        }
      }
    } catch (err) {
      const errorMessage = (err as Error)?.message || 'Có lỗi xảy ra khi tải lên tài nguyên';
      setError(errorMessage);
      if (onUploadError) {
        onUploadError(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const resetForm = () => {
    setFiles([]);
    setTitles({});
    setDescription('');
    setTags([]);
    setTagInput('');
    setSelectedCategories([]);
    setError(null);
    setSuccess(null);

    // File input is handled by dropzone, no need to reset manually
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom>
          Tải Lên Sách Số Mới
        </Typography>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <CheckCircle sx={{ mr: 1 }} />
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Drag & Drop File Upload */}
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragReject ? 'error.main' : isDragActive ? 'primary.main' : '#ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : isDragReject ? 'error.lighter' : 'background.paper',
              transition: 'all 0.2s ease-in-out',
              minHeight: 200,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} id="resource-file-input" />

            <CloudUpload
              sx={{
                fontSize: 64,
                color: isDragActive ? 'primary.main' : isDragReject ? 'error.main' : 'text.secondary',
                mb: 2,
                transition: 'all 0.2s ease-in-out',
                transform: isDragActive ? 'scale(1.1)' : 'scale(1)'
              }}
            />

            <Typography variant="h6" gutterBottom sx={{ fontWeight: isDragActive ? 600 : 400 }}>
              {isDragActive
                ? 'Thả file vào đây để tải lên...'
                : isDragReject
                  ? 'File không hợp lệ! Vui lòng chọn file: PDF, Word, Excel, Ảnh, Video, Audio, Archive'
                  : files.length > 0
                    ? `Đã chọn ${files.length} file${files.length > 1 ? 's' : ''} (tối đa 10)`
                    : 'Kéo thả file vào đây hoặc click để chọn file (tối đa 10 file)'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {isDragActive
                ? 'File sẽ được tải lên ngay khi bạn thả'
                : `Hỗ trợ: PDF, Word, Excel, Ảnh, Video, Audio, Archive (tối đa ${MAX_FILE_SIZE / 1024 / 1024}MB/file, tối đa 10 file)`}
            </Typography>

          </Box>

          {/* File list with individual titles */}
          {files.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Danh sách file đã chọn ({files.length}/10):
              </Typography>
              {files.map((file, index) => (
                <Box key={file.name} sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`Tiêu đề tài nguyên ${index + 1} *`}
                    value={titles[file.name] || ''}
                    onChange={(e) => setTitles(prev => ({ ...prev, [file.name]: e.target.value }))}
                    required
                    disabled={isUploading}
                    helperText={file.name}
                    error={!titles[file.name]?.trim()}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleFileRemove(file.name)}
                    disabled={isUploading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Form Fields - Grid Layout */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

            <Autocomplete
              fullWidth
              multiple
              options={categories || []}
              getOptionLabel={(option) => option.name}
              value={selectedCategories}
              onChange={(event, newValue) => {
                setSelectedCategories(newValue);
              }}
              loading={loadingCategories}
              filterSelectedOptions
              disabled={isUploading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Chủ đề"
                  placeholder="Chọn chủ đề"
                />
              )}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -2, ml: 1, display: 'block' }}>
              💡 Có thể bỏ qua, để AI Hub tự phân loại
            </Typography>
          </Box>

          <Box>
            <TextField
              fullWidth
              label="Mô tả"
              multiline
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isUploading}
              placeholder="Mô tả chi tiết (tùy chọn)"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1, display: 'block' }}>
              💡 Có thể bỏ qua, AI Hub sẽ tự động tóm tắt nội dung
            </Typography>
          </Box>

          {/* Tags Section */}
          <Box>
            <TextField
              fullWidth
              label="Thẻ (Tags)"
              placeholder="Nhập thẻ và nhấn Enter để thêm (tùy chọn)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleTagKeyPress}
              disabled={isUploading}
              helperText="Thêm thẻ để giúp người khác tìm kiếm dễ dàng hơn"
              InputProps={{
                endAdornment: tagInput.trim() && (
                  <Button
                    size="small"
                    onClick={handleTagAdd}
                    disabled={isUploading}
                    sx={{ mr: -1 }}
                  >
                    Thêm
                  </Button>
                )
              }}
            />

            {tags.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                {tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => handleTagRemove(tag)}
                    size="small"
                    disabled={isUploading}
                  />
                ))}
              </Box>
            )}
          </Box>

          {/* Upload Progress */}
          {isUploading && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Đang xử lý file và lưu thông tin...
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={resetForm}
              disabled={isUploading}
            >
              Làm mới
            </Button>

            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={files.length === 0 || Object.values(titles).some(t => !t?.trim()) || isUploading}
              startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {isUploading ? 'Đang tải lên...' : files.length > 1 ? `Tải lên ${files.length} tài nguyên` : 'Tải lên tài nguyên'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
